/**
 * Browser polyfills for Node.js APIs used by @react-pdf/renderer and other libraries
 * 
 * This file provides browser-compatible implementations of Node.js APIs
 * that are required by libraries designed for server-side environments.
 */

// Global polyfill
if (typeof global === 'undefined') {
  window.global = window;
}

// Process polyfill for browser environment
if (typeof process === 'undefined') {
  window.process = {
    env: { NODE_ENV: process?.env?.NODE_ENV || 'development' },
    nextTick: (fn, ...args) => setTimeout(() => fn(...args), 0),
    cwd: () => '/',
    version: 'v16.0.0',
    platform: 'browser',
    browser: true,
    versions: { node: '16.0.0' }
  };
} else {
  // Ensure nextTick exists
  if (!process.nextTick) {
    process.nextTick = (fn, ...args) => setTimeout(() => fn(...args), 0);
  }
  // Ensure env exists
  if (!process.env) {
    process.env = { NODE_ENV: 'development' };
  }
}

// Buffer polyfill (basic implementation for compatibility)
if (typeof Buffer === 'undefined') {
  window.Buffer = {
    isBuffer: () => false,
    from: (data) => new Uint8Array(data),
    alloc: (size) => new Uint8Array(size),
    allocUnsafe: (size) => new Uint8Array(size),
    byteLength: (string, encoding) => new TextEncoder().encode(string).length,
    concat: (list) => {
      const totalLength = list.reduce((acc, buf) => acc + buf.length, 0);
      const result = new Uint8Array(totalLength);
      let offset = 0;
      list.forEach(buf => {
        result.set(buf, offset);
        offset += buf.length;
      });
      return result;
    }
  };
}

/**
 * Initialize all browser polyfills
 * Call this function before using libraries that require Node.js APIs
 */
export const initializePolyfills = () => {
  // Polyfills are initialized when this module is imported
  // This function is provided for explicit initialization if needed
  console.debug('[Polyfills] Browser compatibility polyfills initialized');
};

/**
 * Check if we're running in a browser environment
 */
export const isBrowser = () => typeof window !== 'undefined';

/**
 * Check if Node.js APIs are available
 */
export const hasNodeAPIs = () => typeof process !== 'undefined' && process.versions && process.versions.node;

export default {
  initializePolyfills,
  isBrowser,
  hasNodeAPIs
};